import { AppType } from "client/Api/Apps/common";
import {
	ReactElement,
	useCallback
} from "react";
import { useRenderManager } from "../../useRenderManager";
import { OneByOneTileContent } from "../OneByOneTileContent";
import { TwoByTwoTileContent } from "../TwoByTwoTileContent";
import { NedapOnsLiveTileContent } from "./NedapOnsLiveTileContent";
import { navigateToNedapOnsApp } from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "../useRegisterTileUsage";
import { NedapOnsLiveInformationProvider } from "./provider";
import { TileRenderState } from "../../RenderState/common";
import { useBaseNavigation } from "../useInvokeNavigation";
import { TileContextProvider } from "../../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type NedapOnsTileContentProps = {
	renderState: TileRenderState<AppType.NedapOns>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
	provider: NedapOnsLiveInformationProvider;
};

export const NedapOnsTileContent = (props: NedapOnsTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu,
		provider
	} = props;

	const [navigationElement, navigateHandler] = useNedapOnsTileNavigation(renderState, onEdit);

	const {
		dimension,
		data: { settings }
	} = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<NedapOnsLiveTileContent
				connection={settings.connection}
				destinationWindow={settings.destinationWindow}
				provider={provider}
				tabs={settings.tabs}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		],
		[
			{ columnSpan: 2, rowSpan: 2 },
			() => (
				<TwoByTwoTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
NedapOnsTileContent.displayName = "NedapOnsTileContent";

export function useNedapOnsTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.NedapOns>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	const {
		environmentAppId,
		settings: {
			destinationWindow
		}
	 } = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useCallback(
			(): void => {
				navigateToNedapOnsApp(
					{
						id: environmentAppId,
						destinationWindow: destinationWindow
					},
					() => registerTileUsage()
				);
			},
			[
				destinationWindow,
				environmentAppId,
				registerTileUsage
			]
		)
	);
}
